import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { Button } from './ui/button';
import AdvancedFilters from './AdvancedFilters';
import {
  Server,
  Cpu,
  HardDrive,
  Activity,
  AlertCircle,
  CheckCircle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  Zap,
  Target,
  Gauge,
  AlertTriangle,
  Info
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useMonitoring } from '../context/MonitoringContext';

import { parseUtc } from '../lib/parseUtc';
const EnhancedPodNodeDetails = ({ clusterId }) => {
  // Prefer MonitoringContext's snapshot so the cluster-level numbers shown
  // here line up with the page header + every other Monitoring tab. The
  // local /realtime-metrics fallback below stays as a defensive path for
  // when this component is mounted outside the provider.
  let contextData = null;
  try {
    contextData = useMonitoring();
  } catch (e) {
    contextData = null;
  }
  const [realTimeData, setRealTimeData] = useState(null);
  const [podMetrics, setPodMetrics] = useState([]);
  const [nodeMetrics, setNodeMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [filteredData, setFilteredData] = useState({ pods: [], nodes: [] });
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Apply filters to data
  useEffect(() => {
    if (!podMetrics.length && !nodeMetrics.length) {
      setFilteredData({ pods: [], nodes: [] });
      return;
    }

    const applyFilters = (items, type) => {
      return items.filter(item => {
        // Search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          if (!item.name.toLowerCase().includes(searchLower) &&
            !item.namespace?.toLowerCase().includes(searchLower)) {
            return false;
          }
        }

        // Namespace filter
        if (filters.namespace && filters.namespace !== 'all' && item.namespace !== filters.namespace) {
          return false;
        }

        // Node filter
        if (filters.node && filters.node !== 'all' && item.node !== filters.node) {
          return false;
        }

        // Status filter
        if (filters.status && filters.status !== 'all' && item.status !== filters.status) {
          return false;
        }

        // Resource type filter
        if (filters.resourceType !== 'all') {
          if (filters.resourceType === 'pods' && type !== 'pods') return false;
          if (filters.resourceType === 'nodes' && type !== 'nodes') return false;
        }

        // Usage range filters
        const cpuUsage = parseFloat(item.cpuUsage) || 0;
        const memoryUsage = parseFloat(item.memoryUsage) || 0;
        const cost = parseFloat(item.cost) || 0;
        const efficiency = parseFloat(item.efficiency) || 0;

        if (cpuUsage < filters.cpuUsageRange?.[0] || cpuUsage > filters.cpuUsageRange?.[1]) {
          return false;
        }
        if (memoryUsage < filters.memoryUsageRange?.[0] || memoryUsage > filters.memoryUsageRange?.[1]) {
          return false;
        }
        if (cost < filters.costRange?.[0] || cost > filters.costRange?.[1]) {
          return false;
        }
        if (efficiency < filters.efficiencyRange?.[0] || efficiency > filters.efficiencyRange?.[1]) {
          return false;
        }

        // Problematic filter
        if (filters.showOnlyProblematic) {
          const isProblematic = item.status === 'Failed' ||
            item.restarts > 5 ||
            efficiency < 30 ||
            cpuUsage > 90 ||
            memoryUsage > 90;
          if (!isProblematic) return false;
        }

        // Optimizable filter
        if (filters.showOnlyOptimizable) {
          const isOptimizable = efficiency < 70 ||
            (cpuUsage < 20 && memoryUsage < 20) ||
            cost > 50;
          if (!isOptimizable) return false;
        }

        return true;
      }).sort((a, b) => {
        const sortBy = filters.sortBy || 'name';
        const sortOrder = filters.sortOrder || 'asc';

        let aVal = a[sortBy];
        let bVal = b[sortBy];

        // Handle numeric sorting
        if (['cpuUsage', 'memoryUsage', 'cost', 'efficiency', 'restarts'].includes(sortBy)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        if (sortOrder === 'desc') {
          return bVal > aVal ? 1 : -1;
        }
        return aVal > bVal ? 1 : -1;
      });
    };

    setFilteredData({
      pods: applyFilters(podMetrics, 'pods'),
      nodes: applyFilters(nodeMetrics, 'nodes')
    });
  }, [filters, podMetrics, nodeMetrics]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Get available namespaces and nodes for filter options
  const availableNamespaces = [...new Set(podMetrics.map(pod => pod.namespace).filter(Boolean))];
  const availableNodes = [...new Set([
    ...podMetrics.map(pod => pod.node).filter(Boolean),
    ...nodeMetrics.map(node => node.name).filter(Boolean)
  ])];

  useEffect(() => {
    if (clusterId) {
      fetchEnhancedMetrics();
      // Set up real-time polling every 30 seconds
      intervalRef.current = setInterval(fetchEnhancedMetrics, 30000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clusterId]);

  const fetchEnhancedMetrics = async () => {
    try {
      // Prefer the snapshot from MonitoringContext so we don't show numbers
      // that drift a few seconds from the rest of the page. Fall back to a
      // direct /realtime-metrics fetch only when the context is empty
      // (component mounted outside provider, or first paint).
      let realTimeData;
      if (contextData?.realTimeData) {
        realTimeData = contextData.realTimeData;
      } else {
        const realTimeRes = await axios.get(`${API}/realtime-metrics/${clusterId}`);
        realTimeData = realTimeRes.data;
      }

      setRealTimeData(realTimeData);

      // Enhanced pod metrics — only compute scores when real usage data exists.
      // Previously we filled missing cpu_usage/memory_usage with Math.random(),
      // which produced fake "efficiency" / "waste" numbers. Now we mark pods
      // without live usage as has_live_metrics=false and skip scoring.
      const enhancedPods = (realTimeData.pods || []).map(pod => {
        const hasLiveMetrics = (
          typeof pod.cpu_usage === 'number' && pod.cpu_usage > 0 &&
          typeof pod.memory_usage === 'number' && pod.memory_usage > 0 &&
          typeof pod.cpu_request === 'number' && pod.cpu_request > 0 &&
          typeof pod.memory_request === 'number' && pod.memory_request > 0
        );
        return {
          ...pod,
          status: pod.status || 'Running',
          has_live_metrics: hasLiveMetrics,
          efficiency_score: hasLiveMetrics ? calculatePodEfficiency(pod) : null,
          cost_per_hour: pod.total_hourly_cost || calculatePodCost(pod),
          waste_score: hasLiveMetrics ? calculateWasteScore(pod) : null,
          recommendations: hasLiveMetrics ? generatePodRecommendations(pod) : []
        };
      });

      // Create node metrics from real-time data — prefer node_costs (Infracost) when available
      const nodeCosts = realTimeData.node_costs || [];
      const rawNodes = realTimeData.nodes || [];
      const nodeNames = [...new Set(enhancedPods.map(pod => pod.node).filter(Boolean))];

      const enhancedNodes = nodeNames.map(nodeName => {
        const podsOnNode = enhancedPods.filter(pod => pod.node === nodeName);
        // Find matching Infracost node cost entry
        const costEntry = nodeCosts.find(nc => nc.name === nodeName) || {};
        // Find matching raw node data from the agent
        const rawNode = rawNodes.find(n => n.name === nodeName) || {};
        const labels = rawNode.labels || {};

        const cpuCapacityRaw = rawNode.cpu || rawNode.cpu_capacity;
        const memCapacityRaw = rawNode.memory || rawNode.memory_capacity;
        // Parse cpu capacity: could be "2" (cores) or "2000m"
        let cpuCapacity = 0;
        if (cpuCapacityRaw) {
          const cpuStr = String(cpuCapacityRaw);
          cpuCapacity = cpuStr.endsWith('m') ? parseInt(cpuStr) / 1000 : parseFloat(cpuStr);
        }
        // Parse memory capacity: could be bytes or Ki/Mi/Gi string
        let memCapacity = 0;
        if (memCapacityRaw) {
          const memStr = String(memCapacityRaw);
          if (memStr.endsWith('Ki')) memCapacity = parseInt(memStr) * 1024;
          else if (memStr.endsWith('Mi')) memCapacity = parseInt(memStr) * 1024 * 1024;
          else if (memStr.endsWith('Gi')) memCapacity = parseInt(memStr) * 1024 * 1024 * 1024;
          else memCapacity = parseFloat(memStr);
        }

        const instanceType = costEntry.instance_type || rawNode.instance_type ||
          labels['node.kubernetes.io/instance-type'] || labels['beta.kubernetes.io/instance-type'] || 'unknown';
        const provider = costEntry.provider || rawNode.provider ||
          (labels['eks.amazonaws.com/nodegroup'] ? 'aws' :
            labels['cloud.google.com/gke-nodepool'] ? 'gcp' :
              labels['kubernetes.azure.com/agentpool'] ? 'azure' : 'unknown');
        const region = costEntry.region || rawNode.region ||
          labels['topology.kubernetes.io/region'] || labels['failure-domain.beta.kubernetes.io/region'] || '';

        const hourlyCost = costEntry.hourly_cost || 0;
        const dailyCost = costEntry.daily_cost || hourlyCost * 24;
        const monthlyCost = costEntry.monthly_cost || dailyCost * 30;

        const totalCpuUsage = podsOnNode.reduce((sum, pod) => sum + (pod.cpu_usage || 0), 0);
        const totalMemUsage = podsOnNode.reduce((sum, pod) => sum + (pod.memory_usage || 0), 0);

        const node = {
          name: nodeName,
          pods_count: podsOnNode.length,
          cpu_usage: cpuCapacity > 0 ? (totalCpuUsage / cpuCapacity) * 100 : 0,
          memory_usage: memCapacity > 0 ? (totalMemUsage / memCapacity) * 100 : 0,
          cpu_capacity: cpuCapacity || (rawNode.cpu_capacity || 0),
          memory_capacity: memCapacity || (rawNode.memory_capacity || 0),
          status: rawNode.status || 'Ready',
          instance_type: instanceType,
          provider: provider,
          region: region,
          hourly_cost: hourlyCost,
          daily_cost: dailyCost,
          monthly_cost: monthlyCost,
          labels: labels
        };

        return {
          ...node,
          utilization_score: (node.cpu_usage + node.memory_usage) / 2,
          efficiency_rating: node.cpu_usage > 0 ? Math.min(100, (node.cpu_usage + node.memory_usage) / 2) : 0,
          cost_efficiency: hourlyCost > 0 && node.cpu_usage > 0 ? Math.min(100, (node.cpu_usage / (hourlyCost * 10)) * 100) : 0
        };
      });

      setPodMetrics(enhancedPods);
      setNodeMetrics(enhancedNodes);

      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch enhanced metrics');
      toast.error('Failed to fetch enhanced metrics');
    } finally {
      setLoading(false);
    }
  };

  const calculatePodEfficiency = (pod) => {
    if (!pod.cpu_usage || !pod.memory_usage || !pod.cpu_request || !pod.memory_request) {
      return 0;
    }

    const cpuEfficiency = Math.min((pod.cpu_usage / pod.cpu_request) * 100, 100);
    const memoryEfficiency = Math.min((pod.memory_usage / pod.memory_request) * 100, 100);

    return (cpuEfficiency + memoryEfficiency) / 2;
  };

  const calculatePodCost = (pod) => {
    // Simplified cost calculation based on resource requests
    const cpuCost = (pod.cpu_request || 0) * 0.031; // $0.031 per vCPU per hour
    const memoryCost = ((pod.memory_request || 0) / (1024 * 1024 * 1024)) * 0.0034; // $0.0034 per GB per hour
    return cpuCost + memoryCost;
  };

  const calculateWasteScore = (pod) => {
    if (!pod.cpu_request || !pod.memory_request || !pod.cpu_usage || !pod.memory_usage) {
      return 0;
    }

    const cpuWaste = Math.max(0, pod.cpu_request - pod.cpu_usage);
    const memoryWaste = Math.max(0, pod.memory_request - pod.memory_usage);

    const cpuWastePercent = (cpuWaste / pod.cpu_request) * 100;
    const memoryWastePercent = (memoryWaste / pod.memory_request) * 100;

    return (cpuWastePercent + memoryWastePercent) / 2;
  };

  const generatePodRecommendations = (pod) => {
    const recommendations = [];
    const efficiency = calculatePodEfficiency(pod);
    const waste = calculateWasteScore(pod);

    if (efficiency < 30) {
      recommendations.push({
        type: 'warning',
        message: 'Low resource utilization detected',
        action: 'Consider reducing resource requests'
      });
    }

    if (waste > 50) {
      recommendations.push({
        type: 'error',
        message: 'High resource waste detected',
        action: 'Optimize resource allocation'
      });
    }

    if (pod.restarts > 5) {
      recommendations.push({
        type: 'warning',
        message: 'High restart count',
        action: 'Investigate stability issues'
      });
    }

    return recommendations;
  };

  const calculateNodeUtilization = (node) => {
    // This would be calculated from actual node metrics in a real implementation
    return Math.random() * 100; // Placeholder
  };

  const calculateNodeEfficiency = (node) => {
    // This would be calculated from actual node metrics in a real implementation
    return Math.random() * 100; // Placeholder
  };

  const calculateCostEfficiency = (node) => {
    // This would be calculated from actual cost and utilization data
    return Math.random() * 100; // Placeholder
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatCPU = (cpu) => {
    if (!cpu) return '0m';
    if (cpu < 1) {
      return `${Math.round(cpu * 1000)}m`;
    }
    return `${cpu.toFixed(2)}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'succeeded': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEfficiencyColor = (efficiency) => {
    if (efficiency == null) return 'text-slate-400 dark:text-slate-500';
    if (efficiency >= 80) return 'text-green-600';
    if (efficiency >= 60) return 'text-yellow-600';
    if (efficiency >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getEfficiencyBadge = (efficiency) => {
    if (efficiency == null) return { label: 'No metrics', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' };
    if (efficiency >= 80) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (efficiency >= 60) return { label: 'Good', color: 'bg-yellow-100 text-yellow-800' };
    if (efficiency >= 40) return { label: 'Fair', color: 'bg-orange-100 text-orange-800' };
    return { label: 'Poor', color: 'bg-red-100 text-red-800' };
  };

  const CHART_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Advanced Filters */}
      <AdvancedFilters
        onFiltersChange={handleFiltersChange}
        clusterId={clusterId}
        availableNamespaces={availableNamespaces}
        availableNodes={availableNodes}
      />

      {/* Summary Cards — cluster-aggregate utilization comes from the same
          MonitoringContext snapshot used on every other tab, so the numbers
          match. Per-pod efficiency/waste was removed because pod-level usage
          isn't reliably reported (most pods returned 0, dragging the average
          to nonsense values like 7.1% efficiency + 22% waste). */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Total Pods</p>
              <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
                <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{filteredData.pods.length}</p>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
              {filteredData.pods.filter(p => p.status === 'Running').length} running
            </p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">CPU Utilization</p>
              <div className="p-2 bg-violet-50 dark:bg-violet-500/10 rounded-md">
                <Cpu className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
              {(contextData?.cpuUsage || 0).toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">cluster-wide, live</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Memory Utilization</p>
              <div className="p-2 bg-rose-50 dark:bg-rose-500/10 rounded-md">
                <HardDrive className="w-4 h-4 text-rose-600 dark:text-rose-400" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
              {(contextData?.memoryUsage || 0).toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">cluster-wide, live</p>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Cluster Cost/Hour</p>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
                <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
              {formatCurrency(contextData?.costPerHour || 0)}
            </p>
            <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
              {filteredData.pods.filter(p => p.has_live_metrics).length}/{filteredData.pods.length} pods with live metrics
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pods" className="w-full">
        <TabsList className="grid w-full grid-cols-3 dark:bg-surface-dark">
          <TabsTrigger value="pods">Enhanced Pods ({filteredData.pods.length})</TabsTrigger>
          <TabsTrigger value="nodes">Enhanced Nodes ({filteredData.nodes.length})</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="pods" className="space-y-4">
          {/* Enhanced Pod List */}
          <div className="space-y-4">
            {filteredData.pods.map((pod, index) => {
              const efficiencyBadge = getEfficiencyBadge(pod.efficiency_score);

              return (
                <Card key={index} className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h4 className="font-semibold text-lg dark:text-white">{pod.name}</h4>
                        <p className="text-sm text-gray-600 dark:text-text-secondary">
                          {pod.namespace} • {pod.node}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={getStatusColor(pod.status)}>
                          {pod.status || 'Unknown'}
                        </Badge>
                        <Badge className={efficiencyBadge.color}>
                          {efficiencyBadge.label}
                        </Badge>
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <div className="p-3 bg-blue-100 dark:bg-blue-500/20 rounded-lg inline-block mb-2">
                          <Gauge className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-sm font-medium dark:text-text-secondary">Efficiency</p>
                        <p className={`text-lg font-bold ${getEfficiencyColor(pod.efficiency_score)}`}>
                          {pod.efficiency_score == null ? '—' : `${pod.efficiency_score.toFixed(1)}%`}
                        </p>
                      </div>

                      <div className="text-center">
                        <div className="p-3 bg-green-100 dark:bg-green-500/20 rounded-lg inline-block mb-2">
                          <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <p className="text-sm font-medium dark:text-text-secondary">Cost/Hour</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(pod.cost_per_hour)}
                        </p>
                      </div>

                      <div className="text-center">
                        <div className="p-3 bg-red-100 dark:bg-red-500/20 rounded-lg inline-block mb-2">
                          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <p className="text-sm font-medium dark:text-text-secondary">Waste Score</p>
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">
                          {pod.waste_score == null ? '—' : `${pod.waste_score.toFixed(1)}%`}
                        </p>
                      </div>

                      <div className="text-center">
                        <div className="p-3 bg-purple-100 dark:bg-purple-500/20 rounded-lg inline-block mb-2">
                          <Activity className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <p className="text-sm font-medium dark:text-text-secondary">Restarts</p>
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                          {pod.restarts || 0}
                        </p>
                      </div>
                    </div>

                    {/* Resource Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="space-y-3">
                        <h5 className="font-medium flex items-center dark:text-white">
                          <Cpu className="w-4 h-4 mr-2 text-blue-600" />
                          CPU Resources
                        </h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Usage:</span>
                            <span className="font-mono dark:text-white">{formatCPU(pod.cpu_usage)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Request:</span>
                            <span className="font-mono dark:text-white">{formatCPU(pod.cpu_request)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Limit:</span>
                            <span className="font-mono dark:text-white">{formatCPU(pod.cpu_limit)}</span>
                          </div>
                          <Progress
                            value={Math.min((pod.cpu_usage / pod.cpu_request) * 100, 100)}
                            className="h-2"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h5 className="font-medium flex items-center dark:text-white">
                          <HardDrive className="w-4 h-4 mr-2 text-green-600" />
                          Memory Resources
                        </h5>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Usage:</span>
                            <span className="font-mono dark:text-white">{formatBytes(pod.memory_usage)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Request:</span>
                            <span className="font-mono dark:text-white">{formatBytes(pod.memory_request)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="dark:text-text-secondary">Limit:</span>
                            <span className="font-mono dark:text-white">{formatBytes(pod.memory_limit)}</span>
                          </div>
                          <Progress
                            value={Math.min((pod.memory_usage / pod.memory_request) * 100, 100)}
                            className="h-2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Recommendations */}
                    {pod.recommendations.length > 0 && (
                      <div className="border-t dark:border-surface-border pt-4">
                        <h5 className="font-medium mb-2 flex items-center dark:text-white">
                          <Target className="w-4 h-4 mr-2 text-orange-600" />
                          Recommendations
                        </h5>
                        <div className="space-y-2">
                          {pod.recommendations.map((rec, recIndex) => (
                            <div key={recIndex} className={`p-2 rounded text-sm ${rec.type === 'error' ? 'bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300' : 'bg-yellow-50 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-300'
                              }`}>
                              <div className="font-medium">{rec.message}</div>
                              <div className="text-xs mt-1">{rec.action}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="nodes" className="space-y-4">
          <div className="space-y-4">
            {filteredData.nodes.map((node, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow dark:bg-surface-dark dark:border-surface-border">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="font-semibold text-lg dark:text-white">{node.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-text-secondary">
                        {node.instance_type !== 'unknown' ? `${node.instance_type}` : ''}{node.provider !== 'unknown' ? ` • ${node.provider.toUpperCase()}` : ''}{node.region ? ` • ${node.region}` : ''}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-text-secondary mt-1">{node.pods_count} pods</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold dark:text-white">{formatCurrency(node.daily_cost || 0)}/day</div>
                      <div className="text-sm text-gray-600 dark:text-text-secondary">{formatCurrency(node.hourly_cost || 0)}/hr</div>
                      {node.monthly_cost > 0 && (
                        <div className="text-xs text-gray-500 dark:text-text-secondary">{formatCurrency(node.monthly_cost)}/mo</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="p-3 bg-blue-100 dark:bg-blue-500/20 rounded-lg inline-block mb-2">
                        <Gauge className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <p className="text-sm font-medium dark:text-text-secondary">Utilization</p>
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {node.utilization_score.toFixed(1)}%
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="p-3 bg-green-100 dark:bg-green-500/20 rounded-lg inline-block mb-2">
                        <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <p className="text-sm font-medium dark:text-text-secondary">Efficiency</p>
                      <p className={`text-lg font-bold ${getEfficiencyColor(node.efficiency_rating)}`}>
                        {node.efficiency_rating.toFixed(1)}%
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="p-3 bg-purple-100 dark:bg-purple-500/20 rounded-lg inline-block mb-2">
                        <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <p className="text-sm font-medium dark:text-text-secondary">Cost Efficiency</p>
                      <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                        {node.cost_efficiency.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <h5 className="font-medium mb-2 dark:text-white">CPU Capacity</h5>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="dark:text-text-secondary">Total:</span>
                          <span className="font-mono dark:text-white">{node.cpu_capacity || 'N/A'} cores</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium mb-2 dark:text-white">Memory Capacity</h5>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="dark:text-text-secondary">Total:</span>
                          <span className="font-mono dark:text-white">{formatBytes(node.memory_capacity)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          {/* Summary Stats — efficiency averages only over pods with live
              metrics (others would skew to 0). Critical Pods = pods with
              restarts (a real reliability signal), not a fake efficiency
              threshold. */}
          {(() => {
            const podsWithMetrics = filteredData.pods.filter(p => p.has_live_metrics);
            const avgEff = podsWithMetrics.length > 0
              ? podsWithMetrics.reduce((s, p) => s + (p.efficiency_score || 0), 0) / podsWithMetrics.length
              : null;
            const restartingPods = filteredData.pods.filter(p => (p.restarts || 0) > 0).length;
            return (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Total Pods</p>
                    <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-md">
                      <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">{filteredData.pods.length}</p>
                  <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{podsWithMetrics.length} with live metrics</p>
                </div>

                <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Avg Pod Efficiency</p>
                    <div className="p-2 bg-emerald-50 dark:bg-emerald-500/10 rounded-md">
                      <Gauge className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                    {avgEff === null
                      ? <span className="text-slate-400 dark:text-slate-500 font-normal">—</span>
                      : <>{avgEff.toFixed(1)}<span className="text-base text-slate-400 dark:text-slate-500 font-normal ml-0.5">%</span></>
                    }
                  </p>
                  <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">
                    {avgEff === null ? 'no per-pod usage' : `over ${podsWithMetrics.length} measured pods`}
                  </p>
                </div>

                <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Total Cost/Hour</p>
                    <div className="p-2 bg-violet-50 dark:bg-violet-500/10 rounded-md">
                      <DollarSign className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                    {formatCurrency(filteredData.pods.reduce((sum, pod) => sum + (pod.cost_per_hour || 0), 0))}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">sum of all pod hourly costs</p>
                </div>

                <div className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark rounded-lg p-5 shadow-sm hover:shadow transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary">Restarting Pods</p>
                    <div className="p-2 bg-rose-50 dark:bg-rose-500/10 rounded-md">
                      <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                    </div>
                  </div>
                  <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
                    {restartingPods}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">pods with restart count &gt; 0</p>
                </div>
              </div>
            );
          })()}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Efficiency Distribution — only includes pods with live usage
                metrics; otherwise every pod without usage data would land in
                "Poor" and skew the chart. */}
            <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
              <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
                <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Pod Efficiency Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={(() => {
                        const measured = filteredData.pods.filter(p => p.has_live_metrics);
                        return [
                          { name: 'Excellent', value: measured.filter(p => p.efficiency_score >= 80).length, fill: '#22c55e' },
                          { name: 'Good', value: measured.filter(p => p.efficiency_score >= 60 && p.efficiency_score < 80).length, fill: '#3b82f6' },
                          { name: 'Fair', value: measured.filter(p => p.efficiency_score >= 40 && p.efficiency_score < 60).length, fill: '#f59e0b' },
                          { name: 'Poor', value: measured.filter(p => p.efficiency_score < 40).length, fill: '#ef4444' }
                        ];
                      })()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      dataKey="value"
                    >
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">Excellent (80%+)</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                    <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                    <span className="text-sm font-medium">Good (60-80%)</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-orange-50 rounded">
                    <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                    <span className="text-sm font-medium">Fair (40-60%)</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-50 rounded">
                    <div className="w-4 h-4 rounded-full bg-red-500"></div>
                    <span className="text-sm font-medium">Poor (&lt;40%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cost by Namespace */}
            <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
              <CardHeader className="border-b border-slate-100 dark:border-surface-border pb-3">
                <CardTitle className="text-sm font-semibold text-slate-700 dark:text-white">Cost by Namespace</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={
                    Object.entries(
                      filteredData.pods.reduce((acc, pod) => {
                        acc[pod.namespace] = (acc[pod.namespace] || 0) + (pod.cost_per_hour || 0);
                        return acc;
                      }, {})
                    ).map(([namespace, cost]) => ({ namespace, cost }))
                  }>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="namespace" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Cost/Hour']}
                    />
                    <Bar dataKey="cost" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Last Updated */}
      <div className="text-xs text-gray-500 text-center">
        Last updated: {realTimeData ? parseUtc(realTimeData.timestamp).toLocaleString() : 'Never'}
      </div>
    </div>
  );
};

export default EnhancedPodNodeDetails;